import type { ReactElement } from 'react'
import type { SalesOrder, SalesOrderIdInput } from '@/lib/services/sales-orders/sales-orders.types'
import { OrderDetailPanel } from '@/components/orders/OrderDetailPanel'
import { OrderAsideFormFooter } from '@/components/orders/OrderAside'
import { ORDER_ASIDE_FORM_SURFACE_CLASS } from '@/components/orders/order-aside.styles'
import { serverFieldErrors, useAppForm } from '@/lib/form'
import { ORDER_FIELD_MAX_LENGTHS, ORDER_FIELD_NATIVE_MAX_LENGTHS } from '@/lib/order-field-limits'
import { useUpdateSalesOrderMutation } from '@/lib/services/sales-orders/sales-orders.query'
import { getErrorMessage } from '@/lib/utils'
import { GiftIcon } from 'lucide-react'
import { toast } from 'sonner'

type RecipientFields = 'recipient_name' | 'recipient_phone' | 'recipient_email'
export type RecipientDetailsValues = { [Key in RecipientFields]: NonNullable<SalesOrder[Key]> }

interface RecipientDetailsProps {
  initialData?: Partial<Pick<SalesOrder, 'id' | RecipientFields>>
  closeAside?: () => void
  onSave: (recipient: RecipientDetailsValues) => unknown
  isPending?: boolean
  subtitle?: string
}

export function RecipientDetailsForm({
  initialData,
  closeAside,
  onSave,
  isPending = false,
  subtitle = 'Update who should receive this order and how your team can reach them.',
}: RecipientDetailsProps): ReactElement {
  const form = useAppForm({
    defaultValues: {
      recipient_name: initialData?.recipient_name || '',
      recipient_phone: initialData?.recipient_phone || '',
      recipient_email: initialData?.recipient_email || '',
    },
    validators: {
      onSubmitAsync: async ({ value }) => {
        try {
          await onSave({
            recipient_name: value.recipient_name,
            recipient_phone: value.recipient_phone,
            recipient_email: value.recipient_email,
          })
          toast.success('Recipient details updated successfully')
          closeAside?.()
          return undefined
        } catch (error) {
          toast.error('Recipient details update failed', {
            description: getErrorMessage(error),
          })

          return serverFieldErrors(error)
        }
      },
    },
  })

  return (
    <form.AppForm>
      <form.Form className="flex min-h-full flex-col pt-5 pl-0 sm:pt-6 sm:pl-1">
        <OrderDetailPanel
          title="Recipient"
          subtitle={subtitle}
          icon={GiftIcon}
          tone="brand"
          className="shadow-none"
        >
          <div className={ORDER_ASIDE_FORM_SURFACE_CLASS}>
            <div className="grid gap-y-5">
              <form.AppField name="recipient_name">
                {(field) => (
                  <field.TextInput
                    id={`${initialData?.id}-recipient_name`}
                    label="Recipient Name"
                    placeholder="Who should receive this order?"
                    maxLength={ORDER_FIELD_NATIVE_MAX_LENGTHS.recipient_name}
                    characterMaxLength={ORDER_FIELD_MAX_LENGTHS.recipient_name}
                  />
                )}
              </form.AppField>

              <form.AppField name="recipient_phone">
                {(field) => (
                  <field.PhoneInput
                    id={`${initialData?.id}-recipient_phone`}
                    label="Phone"
                    maxLength={ORDER_FIELD_NATIVE_MAX_LENGTHS.recipient_phone}
                    characterMaxLength={ORDER_FIELD_MAX_LENGTHS.recipient_phone}
                  />
                )}
              </form.AppField>

              <form.AppField name="recipient_email">
                {(field) => (
                  <field.EmailInput
                    id={`${initialData?.id}-recipient_email`}
                    label="Email"
                    placeholder="recipient@example.com"
                    maxLength={ORDER_FIELD_NATIVE_MAX_LENGTHS.recipient_email}
                    characterMaxLength={ORDER_FIELD_MAX_LENGTHS.recipient_email}
                  />
                )}
              </form.AppField>
            </div>
          </div>
        </OrderDetailPanel>
        <OrderAsideFormFooter
          form={form}
          mutation={{ isPending }}
          saveLabel="Save Recipient"
          onCancel={closeAside}
        />
      </form.Form>
    </form.AppForm>
  )
}

function EditRecipientDetailsForm({
  initialData,
  closeAside,
  salesOrderId,
}: Pick<RecipientDetailsProps, 'initialData' | 'closeAside'> & {
  salesOrderId: SalesOrderIdInput
}): ReactElement {
  const { mutateAsync: updateSalesOrder, isPending } = useUpdateSalesOrderMutation()

  return (
    <RecipientDetailsForm
      initialData={initialData}
      closeAside={closeAside}
      isPending={isPending}
      onSave={(recipient) => updateSalesOrder({ id: salesOrderId, ...recipient })}
    />
  )
}

export default EditRecipientDetailsForm
